import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { MultiSectionLayout } from "@jugl-web/ui-components";
import {
  SideMenuDivider,
  SideMenuList,
  SideMenuListItem,
  SideMenuPanel,
} from "@jugl-web/ui-kit/layout/SideMenuLayout";
import { useTranslations } from "@jugl-web/utils";
import { FC, Fragment, useMemo, useState } from "react";
import { ReactComponent as ChatStartersActiveIcon } from "./assets/chat-starters-active.svg";
import { ReactComponent as ChatStartersIcon } from "./assets/chat-starters.svg";
import { ReactComponent as GeneralInfoActiveIcon } from "./assets/general-info-active.svg";
import { ReactComponent as GeneralInfoIcon } from "./assets/general-info.svg";
import { ReactComponent as GeneralSetupActiveIcon } from "./assets/general-setup-active.svg";
import { ReactComponent as GeneralSetupIcon } from "./assets/general-setup.svg";
import { ReactComponent as InstallActiveIcon } from "./assets/install-active.svg";
import { ReactComponent as InstallIcon } from "./assets/install.svg";
import { ReactComponent as LauncherStyleActiveIcon } from "./assets/launcher-style-active.svg";
import { ReactComponent as LauncherStyleIcon } from "./assets/launcher-style.svg";
import { ReactComponent as QuickActionsActiveIcon } from "./assets/quick-actions-active.svg";
import { ReactComponent as QuickActionsIcon } from "./assets/quick-actions.svg";
import { CTATabContent } from "./components/CTATabContent";
import { GeneralInfoTabContent } from "./components/GeneralInfoTabContent";
import { GeneralSetupTabContent } from "./components/GeneralSetupTabContent";
import { InstallTabContent } from "./components/InstallTabContent";
import { LauncherStyleTabContent } from "./components/LauncherStyleTabContent";
import { SuggestionsTabContent } from "./components/SuggestionsTabContent";
import { WebChatBotAccountMenuTab } from "./types";

export const WebChatBotAccountScreen: FC = () => {
  const [selectedMenuTab, setSelectedMenuTab] =
    useState<WebChatBotAccountMenuTab>(WebChatBotAccountMenuTab.generalInfo);

  const { requestNavigation } = useNavigationGuardContext();

  const { t } = useTranslations();

  const sideMenuSections = useMemo(
    () => [
      [
        {
          id: WebChatBotAccountMenuTab.generalInfo,
          label: t({
            id: "client-support-page.general-info",
            defaultMessage: "General Info",
          }),
          startIcon:
            selectedMenuTab === WebChatBotAccountMenuTab.generalInfo ? (
              <GeneralInfoActiveIcon />
            ) : (
              <GeneralInfoIcon />
            ),
        },
      ],
      [
        {
          id: WebChatBotAccountMenuTab.generalSetup,
          label: t({
            id: "client-support-page.general-setup",
            defaultMessage: "General Setup",
          }),
          startIcon:
            selectedMenuTab === WebChatBotAccountMenuTab.generalSetup ? (
              <GeneralSetupActiveIcon />
            ) : (
              <GeneralSetupIcon />
            ),
        },
        {
          id: WebChatBotAccountMenuTab.launcherStyle,
          label: t({
            id: "client-support-page.launcher-style",
            defaultMessage: "Launcher Style",
          }),
          startIcon:
            selectedMenuTab === WebChatBotAccountMenuTab.launcherStyle ? (
              <LauncherStyleActiveIcon />
            ) : (
              <LauncherStyleIcon />
            ),
        },
        {
          id: WebChatBotAccountMenuTab.cta,
          label: t({
            id: "client-support-page.chat-starters",
            defaultMessage: "Chat Starters",
          }),
          startIcon:
            selectedMenuTab === WebChatBotAccountMenuTab.cta ? (
              <ChatStartersActiveIcon />
            ) : (
              <ChatStartersIcon />
            ),
        },
        {
          id: WebChatBotAccountMenuTab.suggestions,
          label: t({
            id: "client-support-page.quick-actions",
            defaultMessage: "Quick Actions",
          }),
          startIcon:
            selectedMenuTab === WebChatBotAccountMenuTab.suggestions ? (
              <QuickActionsActiveIcon />
            ) : (
              <QuickActionsIcon />
            ),
        },
      ],
      [
        {
          id: WebChatBotAccountMenuTab.install,
          label: t({
            id: "client-support-page.install",
            defaultMessage: "Install",
          }),
          startIcon:
            selectedMenuTab === WebChatBotAccountMenuTab.install ? (
              <InstallActiveIcon />
            ) : (
              <InstallIcon />
            ),
        },
      ],
    ],
    [selectedMenuTab, t]
  );

  return (
    <MultiSectionLayout
      leftSide={
        <SideMenuPanel>
          {sideMenuSections.map((section, index) => {
            const isLastSection = index === sideMenuSections.length - 1;

            return (
              <Fragment key={+index}>
                <SideMenuList>
                  {section.map((item) => (
                    <SideMenuListItem
                      key={item.id}
                      label={item.label}
                      startIcon={item.startIcon}
                      isSelected={selectedMenuTab === item.id}
                      onClick={() =>
                        requestNavigation(() => setSelectedMenuTab(item.id))
                      }
                    />
                  ))}
                </SideMenuList>
                {!isLastSection && <SideMenuDivider />}
              </Fragment>
            );
          })}
        </SideMenuPanel>
      }
    >
      {selectedMenuTab === WebChatBotAccountMenuTab.generalInfo && (
        <GeneralInfoTabContent />
      )}
      {selectedMenuTab === WebChatBotAccountMenuTab.generalSetup && (
        <GeneralSetupTabContent />
      )}
      {selectedMenuTab === WebChatBotAccountMenuTab.launcherStyle && (
        <LauncherStyleTabContent />
      )}
      {selectedMenuTab === WebChatBotAccountMenuTab.cta && <CTATabContent />}
      {selectedMenuTab === WebChatBotAccountMenuTab.suggestions && (
        <SuggestionsTabContent />
      )}
      {selectedMenuTab === WebChatBotAccountMenuTab.install && (
        <InstallTabContent />
      )}
    </MultiSectionLayout>
  );
};
